import { create } from 'zustand';

export interface Note {
  id: string;
  content: string;
  createdAt: number;
  updatedAt: number;
}

interface QuickNotesState {
  isOpen: boolean;
  isDetached: boolean;
  notes: Note[];
  activeNoteId: string | null;

  open: () => void;
  close: () => void;
  toggleDetached: () => void;
  addNote: () => void;
  updateNote: (id: string, content: string) => void;
  deleteNote: (id: string) => void;
  setActiveNote: (id: string | null) => void;
}

const STORAGE_KEY = 'quick-notes';
const MAX_NOTES = 50;

// Load notes from localStorage
const loadNotes = (): Note[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save notes to localStorage
const saveNotes = (notes: Note[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
  } catch {
    // Storage full or unavailable
  }
};

// Generate unique ID
const generateId = () => `note-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const useQuickNotesStore = create<QuickNotesState>((set, get) => ({
  isOpen: false,
  isDetached: false,
  notes: loadNotes(),
  activeNoteId: null,

  open: () => {
    const { notes } = get();
    set({
      isOpen: true,
      activeNoteId: notes.length > 0 ? notes[0].id : null,
    });
  },

  close: () => set({ isOpen: false, isDetached: false }),

  toggleDetached: () => set((state) => ({ isDetached: !state.isDetached })),

  addNote: () => {
    const { notes } = get();
    const now = Date.now();
    const newNote: Note = {
      id: generateId(),
      content: '',
      createdAt: now,
      updatedAt: now,
    };

    const updatedNotes = [newNote, ...notes].slice(0, MAX_NOTES);
    saveNotes(updatedNotes);

    set({
      notes: updatedNotes,
      activeNoteId: newNote.id,
    });
  },

  updateNote: (id: string, content: string) => {
    const { notes } = get();
    const updatedNotes = notes.map((note) =>
      note.id === id ? { ...note, content, updatedAt: Date.now() } : note,
    );
    saveNotes(updatedNotes);
    set({ notes: updatedNotes });
  },

  deleteNote: (id: string) => {
    const { notes, activeNoteId } = get();
    const updatedNotes = notes.filter((note) => note.id !== id);
    saveNotes(updatedNotes);

    // If we deleted the active note, select the first one
    const newActiveId = activeNoteId === id ? (updatedNotes[0]?.id || null) : activeNoteId;

    set({
      notes: updatedNotes,
      activeNoteId: newActiveId,
    });
  },

  setActiveNote: (id: string | null) => set({ activeNoteId: id }),
}));
